import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * The bag.
 *
 * **Only identifiers and quantities are stored** - never a title, never a
 * price, never an image. Those are server state, and copying them here would
 * mean a price change on the backend leaves a stale figure in the customer's
 * bag until they clear their browser. The cart's display data is read from the
 * product queries in TanStack Query and joined at render time.
 *
 * That split is also what makes this safe to persist: a variant id and a
 * quantity are the customer's *intent*, which genuinely belongs on their
 * device and should survive a refresh.
 *
 * Pricing is never trusted from here either. Checkout sends line identifiers
 * and quantities; the server recomputes every amount.
 */

export interface CartLine {
  variantId: string;
  /** Kept so the cart can fetch its own product data without a lookup table. */
  productSlug: string;
  quantity: number;
  addedAt: number;
}

interface CartState {
  lines: CartLine[];
  add: (line: Omit<CartLine, 'addedAt' | 'quantity'> & { quantity?: number }) => void;
  setQuantity: (variantId: string, quantity: number) => void;
  remove: (variantId: string) => void;
  clear: () => void;
}

const MAX_QUANTITY_PER_LINE = 10;

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],

      add: ({ variantId, productSlug, quantity = 1 }) =>
        set((state) => {
          const existing = state.lines.find((line) => line.variantId === variantId);
          if (existing) {
            return {
              lines: state.lines.map((line) =>
                line.variantId === variantId
                  ? { ...line, quantity: Math.min(line.quantity + quantity, MAX_QUANTITY_PER_LINE) }
                  : line,
              ),
            };
          }
          return {
            lines: [
              ...state.lines,
              { variantId, productSlug, quantity: Math.min(quantity, MAX_QUANTITY_PER_LINE), addedAt: Date.now() },
            ],
          };
        }),

      setQuantity: (variantId, quantity) =>
        set((state) => ({
          lines:
            quantity <= 0
              ? state.lines.filter((line) => line.variantId !== variantId)
              : state.lines.map((line) =>
                  line.variantId === variantId
                    ? { ...line, quantity: Math.min(quantity, MAX_QUANTITY_PER_LINE) }
                    : line,
                ),
        })),

      remove: (variantId) =>
        set((state) => ({ lines: state.lines.filter((line) => line.variantId !== variantId) })),

      clear: () => set({ lines: [] }),
    }),
    {
      name: 'nsc.bag.v1',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/** Total item count, for the header badge. */
export function useCartCount(): number {
  return useCartStore((state) => state.lines.reduce((total, line) => total + line.quantity, 0));
}
